import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { resolveLegalLink } from '../../lib/legalLinks';
import { getContactTypeLabel, getUiText } from '../../lib/uiText';
import { resolveUrlForLocale } from '../../lib/localizedSlugs';
import { Link } from 'react-router-dom';
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaYoutube,
  FaTiktok,
  FaWhatsapp,
  FaPinterestP,
} from 'react-icons/fa6';
import styles from './Footer.module.css';

const Footer = () => {
  const { footer, companyInfo, legalRouteMap, loading, locale, homeLogoPath } = useGlobal();

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <footer id="site-footer" className={`${styles.footer} ${styles['footer--loading']}`}>
      </footer>
    );
  }

  if (!footer) return null;

  const logoMedia = footer.useCompanyLogo && companyInfo?.logoMain
    ? getStrapiMediaAttributes(companyInfo.logoMain)
    : getStrapiMediaAttributes(footer.logo);

  const creatorLogoMedia = getStrapiMediaAttributes(footer.creatorLogo);
  const footerBackgroundColor = footer?.colorPersonalizado?.trim();
  const useCustomFooterBackground = footerBackgroundColor
    && (typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || CSS.supports('color', footerBackgroundColor));
  const footerStyle = useCustomFooterBackground
    ? { '--color-header-footer': footerBackgroundColor }
    : undefined;
  const footerLogoHeight = footer?.heightLogo?.trim();
  const footerLogoHeightMobile = footer?.heightLogoMobile?.trim();
  const footerStyleWithLogoHeight = {
    ...(footerStyle || {}),
    ...(footerLogoHeight ? { '--footer-logo-height': footerLogoHeight } : {}),
    ...(footerLogoHeightMobile ? { '--footer-logo-height-mobile': footerLogoHeightMobile } : {}),
  };
  const resolvedFooterStyle = Object.keys(footerStyleWithLogoHeight).length > 0
    ? footerStyleWithLogoHeight
    : undefined;
  const copyrightText = typeof footer.copyright === 'string' ? footer.copyright.trim() : '';
  const creatorUrl = typeof footer.creatorUrl === 'string' ? footer.creatorUrl.trim() : '';
  const logoTo = homeLogoPath;

  return (
    <footer
      id="site-footer"
      className={`${styles.footer} ${footer?.textoOscuro ? styles['footer--dark-text'] : ''}`}
      style={resolvedFooterStyle}
    >
      <div className={styles['footer__scroll-top']}>
        <button
          type="button"
          className={styles['footer__scroll-top-button']}
          onClick={handleScrollTop}
          aria-label={getUiText(locale, 'scrollTop')}
        >
          <span className={styles['footer__scroll-top-icon-wrap']} aria-hidden="true">
            <svg
              className={styles['footer__scroll-top-icon']}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 20V4M5 11L12 4L19 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>
      <div className={styles.footer__container}>
        <div className={styles.footer__main}>
          <div className={styles.footer__brand}>
            {logoMedia.src && (
              <Link to={logoTo} className={styles.footer__logo} title={getUiText(locale, 'goHome')}>
                <img src={logoMedia.src} alt={logoMedia.alt} title={logoMedia.title} />
              </Link>
            )}
            {footer.description && (
              <p className={styles.footer__description}>{footer.description}</p>
            )}
            {footer.showSocialMedia && companyInfo?.socialMedia?.length > 0 && (
              <div className={styles['footer__social-links']}>
                {companyInfo.socialMedia.map((item, index) => (
                  <a
                    key={index}
                    href={item.url}
                    className={styles['footer__social-link']}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.platform}
                    title={item.platform}
                  >
                    <SocialIcon platform={item.platform} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {footer.navigation?.length > 0 && (
            <nav className={styles.footer__nav}>
              <h4 className={styles['footer__nav-title']}>{getUiText(locale, 'links')}</h4>
              {footer.navigation.map((item, index) => (
                <a
                  key={index}
                  href={resolveUrlForLocale(item.url, locale) || `#${item.scrollToSection}`}
                  className={styles['footer__nav-link']}
                  target={item.openInNewTab ? '_blank' : undefined}
                  rel={item.isExternal ? 'noopener noreferrer' : undefined}
                  title={item.label}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          )}

          {footer.showContactInfo && companyInfo?.contactInfo?.length > 0 && (
            <div className={styles.footer__contact}>
              <h4 className={styles['footer__contact-title']}>{getUiText(locale, 'contact')}</h4>
              {companyInfo.contactInfo.map((item, index) => (
                <ContactItem key={index} item={item} locale={locale} />
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer__bottom}>
          <div className={styles['footer__bottom-text']}>
            {footer.legalLinks?.length > 0 && (
              <div className={styles.footer__legal}>
                {footer.legalLinks.map((item, index) => {
                  const resolvedLink = resolveLegalLink(item.url, legalRouteMap);
                  const target = resolvedLink.mode === 'file' || resolvedLink.mode === 'external'
                    ? '_blank'
                    : undefined;
                  const rel = target ? 'noopener noreferrer' : undefined;

                  return (
                    <a
                      key={index}
                      href={resolvedLink.href}
                      className={styles['footer__legal-link']}
                      target={target}
                      rel={rel}
                      title={item.label}
                    >
                      {item.label}
                    </a>
                  );
                })}
              </div>
            )}

            {copyrightText && (
              <div className={styles.footer__copyright}>{copyrightText}</div>
            )}
          </div>

          {creatorLogoMedia.src && creatorUrl && (
            <a
              href={creatorUrl}
              className={styles.footer__creator}
              target="_blank"
              rel="noopener noreferrer"
              title={footer.creatorName || undefined}
            >
              <img src={creatorLogoMedia.src} alt={creatorLogoMedia.alt} title={creatorLogoMedia.title} />
            </a>
          )}
        </div>
      </div>
    </footer>
  );
};

const ContactItem = ({ item, locale }) => {
  const isAddress = item.__component === 'shared.address-info';

  const getHref = () => {
    if (isAddress) {
      return item.mapLink || null;
    }

    switch (item.type) {
      case 'phone':
        return `tel:${item.value}`;
      case 'email':
        return `mailto:${item.value || ''}`;
      case 'whatsapp':
        return `https://wa.me/${item.value.replace(/\D/g, '')}`;
      default:
        return null;
    }
  };

  const href = getHref();
  const label = typeof item.label === 'string' ? item.label.trim() : '';
  const accessibleLabel = label || getContactTypeLabel(locale, isAddress ? 'address' : item.type);
  const value = item.value || '';
  const isExternal = href?.startsWith('http');
  const title = value
    ? [accessibleLabel, value].filter(Boolean).join(': ')
    : accessibleLabel;

  return (
    <div className={styles['footer__contact-item']}>
      {href ? (
        <a
          href={href}
          className={styles['footer__contact-link']}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          title={title || undefined}
        >
          {label && <span className={styles['footer__contact-label']}>{label}</span>}
          {value && <span className={styles['footer__contact-value']}>{value}</span>}
        </a>
      ) : (
        <div className={styles['footer__contact-text']}>
          {label && <span className={styles['footer__contact-label']}>{label}</span>}
          {value && <span className={styles['footer__contact-value']}>{value}</span>}
        </div>
      )}
    </div>
  );
};

const SOCIAL_ICONS = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  twitter: FaXTwitter,
  linkedin: FaLinkedinIn,
  youtube: FaYoutube,
  tiktok: FaTiktok,
  whatsapp: FaWhatsapp,
  pinterest: FaPinterestP,
};

const SocialIcon = ({ platform }) => {
  const Icon = SOCIAL_ICONS[platform];
  if (!Icon) return null;
  return <Icon aria-hidden="true" />;
};

export default Footer;
